import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Plus, Trash2, Download, Image as ImageIcon, Save, FolderOpen, FileText, Instagram, Music2, Youtube } from "lucide-react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NICHES = ["Tech & Gadget", "Lifestyle", "Edukasi", "Beauty", "Food", "Fashion", "Finance", "Gaming", "Travel", "Parenting", "Fitness & Health", "Otomotif", "Lainnya"];

const PLATFORMS = [
  { key: "Instagram", icon: Instagram },
  { key: "TikTok", icon: Music2 },
  { key: "YouTube", icon: Youtube },
];

const FORMATS = [
  { key: "feed", label: "Feed Post / Grid Post", mult: 1.0 },
  { key: "reels", label: "Reels / Video Pendek", mult: 1.4 },
  { key: "story", label: "Story (24 jam)", mult: 0.5 },
  { key: "carousel", label: "Carousel / Multi-image", mult: 1.2 },
  { key: "youtube", label: "YouTube Long-form", mult: 2.0 },
  { key: "placement", label: "Product Placement Only", mult: 0.7 },
  { key: "bundle", label: "Bundle (Feed + Story + Reels)", mult: 2.2 },
];

const TIERS = [
  { key: "nano", label: "Nano Creator", baseMin: 15000, baseMax: 30000, defMult: 1.3 },
  { key: "micro", label: "Micro Creator", baseMin: 10000, baseMax: 20000, defMult: 1.0 },
  { key: "mid", label: "Mid-tier Creator", baseMin: 8000, baseMax: 15000, defMult: 1.0 },
  { key: "macro", label: "Macro Creator", baseMin: 5000, baseMax: 12000, defMult: 0.8 },
];

const getTier = (f) => (f >= 200000 ? TIERS[3] : f >= 50000 ? TIERS[2] : f >= 10000 ? TIERS[1] : TIERS[0]);
const erMult = (er) => (er < 2 ? 0.8 : er < 5 ? 1.0 : er <= 8 ? 1.3 : 1.6);
const roundRp = (v) => Math.round(v / 5000) * 5000;
const fmtRp = (v) => "Rp" + Math.round(v).toLocaleString("id-ID");
const fmtShort = (v) => {
  if (v >= 1000000) return (Math.round((v / 1000000) * 10) / 10).toString().replace(".", ",") + "jt";
  return Math.round(v / 1000) + "rb";
};

function computeRate(followers, erStr, formatMult) {
  const f = Number(followers) || 0;
  if (f <= 0) return null;
  const tier = getTier(f);
  const per1000 = f / 1000;
  const er = String(erStr || "").trim();
  if (er !== "") {
    const mid = (tier.baseMin + tier.baseMax) / 2;
    const val = mid * per1000 * erMult(Number(er)) * formatMult;
    return { type: "single", value: roundRp(val), display: fmtRp(roundRp(val)) };
  }
  const lo = tier.baseMin * per1000 * tier.defMult * formatMult;
  const hi = tier.baseMax * per1000 * tier.defMult * formatMult;
  return { type: "range", lo: roundRp(lo), hi: roundRp(hi), display: `Rp${fmtShort(roundRp(lo))} – Rp${fmtShort(roundRp(hi))}` };
}

const emptyPlatform = () => ({ platform: "Instagram", handle: "", followers: "", er: "" });

export default function RateCardGenerator() {
  const [tab, setTab] = useState("buat");
  const [creator, setCreator] = useState({ name: "", tagline: "", location: "", email: "", niche: "" });
  const [platforms, setPlatforms] = useState([emptyPlatform()]);
  const [formats, setFormats] = useState(["feed", "reels", "story"]);
  const [saved, setSaved] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const previewRef = useRef(null);

  const loadSaved = () => api.get("/ratecards").then((r) => setSaved(r.data)).catch(() => {});

  useEffect(() => {
    loadSaved();
    api.get("/identities").then((r) => {
      if (r.data.length > 0) {
        const id = r.data[0];
        setPrefill({ name: id.name || "", niche: (id.answers && id.answers.niche) || "" });
      }
    }).catch(() => {});
  }, []);

  // Prefill from Identitas Kreator (only when fields are still empty)
  useEffect(() => {
    if (!prefill) return;
    setCreator((c) => ({
      ...c,
      name: c.name || prefill.name,
      niche: c.niche || (NICHES.includes(prefill.niche) ? prefill.niche : c.niche),
    }));
  }, [prefill]);

  const updatePlatform = (i, key, val) => setPlatforms((ps) => ps.map((p, idx) => (idx === i ? { ...p, [key]: val } : p)));
  const addPlatform = () => setPlatforms((ps) => [...ps, emptyPlatform()]);
  const removePlatform = (i) => setPlatforms((ps) => (ps.length > 1 ? ps.filter((_, idx) => idx !== i) : ps));
  const toggleFormat = (k) => setFormats((fs) => (fs.includes(k) ? fs.filter((x) => x !== k) : [...fs, k]));

  const validPlatforms = platforms.filter((p) => Number(p.followers) > 0);
  const topPlatform = validPlatforms.reduce((a, b) => (Number(b.followers) > Number(a?.followers || 0) ? b : a), null);
  const tier = topPlatform ? getTier(Number(topPlatform.followers)) : null;
  const selectedFormats = FORMATS.filter((f) => formats.includes(f.key));

  const resetForm = () => {
    setCreator({ name: prefill?.name || "", tagline: "", location: "", email: "", niche: prefill?.niche && NICHES.includes(prefill.niche) ? prefill.niche : "" });
    setPlatforms([emptyPlatform()]);
    setFormats(["feed", "reels", "story"]);
    setCurrentId(null);
  };

  const save = async () => {
    if (!creator.name.trim()) { toast.error("Isi nama tampilan dulu ya"); return; }
    const payload = { title: creator.name.trim(), data: { creator, platforms, formats } };
    try {
      if (currentId) {
        await api.put(`/ratecards/${currentId}`, payload);
        toast.success("Rate card diperbarui");
      } else {
        const r = await api.post("/ratecards", payload);
        setCurrentId(r.data.id);
        toast.success("Rate card tersimpan ke akun kamu");
      }
      loadSaved();
    } catch (e) { toast.error("Gagal menyimpan. Coba lagi ya"); }
  };

  const loadCard = (card) => {
    const d = card.data || {};
    setCreator({ name: "", tagline: "", location: "", email: "", niche: "", ...(d.creator || {}) });
    setPlatforms(d.platforms && d.platforms.length ? d.platforms : [emptyPlatform()]);
    setFormats(d.formats && d.formats.length ? d.formats : ["feed"]);
    setCurrentId(card.id);
    setTab("buat");
    toast.success("Rate card dimuat");
  };

  const deleteCard = async (id) => {
    if (!window.confirm("Hapus rate card ini?")) return;
    await api.delete(`/ratecards/${id}`);
    if (currentId === id) setCurrentId(null);
    loadSaved();
    toast.success("Rate card dihapus");
  };

  const captureCanvas = async () => html2canvas(previewRef.current, { scale: 2, backgroundColor: "#FFFFFF", useCORS: true, logging: false });

  const exportPNG = async () => {
    if (!topPlatform) { toast.error("Isi minimal 1 platform dengan followers"); return; }
    setExporting(true);
    try {
      const canvas = await captureCanvas();
      const link = document.createElement("a");
      link.download = `rate-card-${(creator.name || "studio-kreator").replace(/\s+/g, "-").toLowerCase()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Gambar rate card diunduh");
    } catch (e) { toast.error("Gagal membuat gambar"); } finally { setExporting(false); }
  };

  const exportPDF = async () => {
    if (!topPlatform) { toast.error("Isi minimal 1 platform dengan followers"); return; }
    setExporting(true);
    try {
      const canvas = await captureCanvas();
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const ratio = Math.min((pw - margin * 2) / canvas.width, (ph - margin * 2) / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      pdf.addImage(img, "PNG", (pw - w) / 2, margin, w, h);
      pdf.save(`rate-card-${(creator.name || "studio-kreator").replace(/\s+/g, "-").toLowerCase()}.pdf`);
      toast.success("PDF rate card diunduh");
    } catch (e) { toast.error("Gagal membuat PDF"); } finally { setExporting(false); }
  };

  const today = new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  const inputCls = "w-full rounded-xl bg-sk-bg border border-sk-line px-4 py-2.5 text-sm text-sk-cream placeholder:text-sk-muted/60 focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent";

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sk-brand font-semibold uppercase tracking-wide text-xs mb-2">Media kit</p>
          <h1 className="font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Rate Card Generator</h1>
          <p className="text-sk-muted mt-2 max-w-xl leading-relaxed">Susun daftar harga endorse profesional dari data followers & engagement kamu. Semua dihitung otomatis — tinggal unduh & kirim ke brand.</p>
        </div>
        <div className="flex rounded-full border border-sk-line p-1 bg-sk-surface">
          <button data-testid="tab-buat" onClick={() => setTab("buat")} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === "buat" ? "bg-sk-brand text-sk-ink" : "text-sk-muted"}`}>Buat</button>
          <button data-testid="tab-tersimpan" onClick={() => setTab("tersimpan")} className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${tab === "tersimpan" ? "bg-sk-brand text-sk-ink" : "text-sk-muted"}`}><FolderOpen className="h-4 w-4" /> Tersimpan ({saved.length})</button>
        </div>
      </div>

      {tab === "tersimpan" ? (
        <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 sm:p-8 shadow-sm">
          {saved.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-10 w-10 text-sk-brand/50 mx-auto mb-3" />
              <p className="font-bold text-sk-cream">Belum ada rate card tersimpan</p>
              <p className="text-sk-muted text-sm mt-2">Buat rate card di tab "Buat", lalu simpan biar bisa dibuka lagi kapan saja.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {saved.map((c) => (
                <div key={c.id} data-testid={`saved-ratecard-${c.id}`} className="rounded-xl border border-sk-line bg-sk-bg p-4 flex items-center justify-between gap-4">
                  <button onClick={() => loadCard(c)} data-testid={`load-ratecard-${c.id}`} className="flex-1 min-w-0 text-left">
                    <p className="font-bold text-sk-cream truncate">{c.title}</p>
                    <p className="text-xs text-sk-muted mt-1">Diperbarui {new Date(c.updated_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</p>
                  </button>
                  <button onClick={() => deleteCard(c.id)} data-testid={`delete-ratecard-${c.id}`} className="shrink-0 text-sk-muted hover:text-sk-brand p-2 transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          {/* FORM */}
          <div className="space-y-5">
            {/* Info Kreator */}
            <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 shadow-sm">
              <h3 className="font-bold text-sk-cream mb-4">Info Kreator</h3>
              <div className="grid gap-3">
                <div>
                  <label className="block text-xs font-semibold text-sk-muted mb-1.5">Nama tampilan</label>
                  <input data-testid="ratecard-name-input" value={creator.name} onChange={(e) => setCreator({ ...creator, name: e.target.value })} placeholder="cth: Ridho Harry Azhar" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-sk-muted mb-1.5">Tagline / bio singkat <span className="font-normal">(opsional)</span></label>
                  <input data-testid="ratecard-tagline" value={creator.tagline} onChange={(e) => setCreator({ ...creator, tagline: e.target.value })} placeholder="cth: Review gadget jujur tanpa gimmick" className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-sk-muted mb-1.5">Lokasi <span className="font-normal">(opsional)</span></label>
                    <input data-testid="ratecard-location" value={creator.location} onChange={(e) => setCreator({ ...creator, location: e.target.value })} placeholder="cth: Jakarta" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-sk-muted mb-1.5">Email kontak <span className="font-normal">(opsional)</span></label>
                    <input data-testid="ratecard-email" value={creator.email} onChange={(e) => setCreator({ ...creator, email: e.target.value })} placeholder="cth: halo@email.com" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-sk-muted mb-1.5">Niche konten</label>
                  <Select value={creator.niche} onValueChange={(v) => setCreator({ ...creator, niche: v })}>
                    <SelectTrigger className="w-full bg-sk-bg border-sk-line rounded-xl text-sk-cream" data-testid="ratecard-niche-select"><SelectValue placeholder="Pilih niche" /></SelectTrigger>
                    <SelectContent className="bg-sk-surface border-sk-line text-sk-cream">
                      {NICHES.map((n) => <SelectItem key={n} value={n} data-testid={`niche-opt-${n}`}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Platforms */}
            <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-sk-cream">Platform</h3>
                <button data-testid="add-platform-btn" onClick={addPlatform} className="flex items-center gap-1.5 rounded-full border border-sk-line px-3 py-1.5 text-xs font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors"><Plus className="h-3.5 w-3.5" /> Tambah</button>
              </div>
              <div className="space-y-4">
                {platforms.map((p, i) => (
                  <div key={i} className="rounded-xl border border-sk-line bg-sk-bg p-4" data-testid={`platform-row-${i}`}>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <Select value={p.platform} onValueChange={(v) => updatePlatform(i, "platform", v)}>
                        <SelectTrigger className="w-[150px] bg-sk-surface border-sk-line rounded-lg text-sk-cream h-9" data-testid={`platform-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-sk-surface border-sk-line text-sk-cream">
                          {PLATFORMS.map((pl) => <SelectItem key={pl.key} value={pl.key} data-testid={`platform-opt-${pl.key}-${i}`}>{pl.key}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {platforms.length > 1 && (
                        <button data-testid={`remove-platform-${i}`} onClick={() => removePlatform(i)} className="text-sk-muted hover:text-sk-brand p-1.5 transition-colors"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <input data-testid={`platform-handle-${i}`} value={p.handle} onChange={(e) => updatePlatform(i, "handle", e.target.value)} placeholder="@username" className={inputCls + " sm:col-span-3"} />
                      <input data-testid={`platform-followers-${i}`} type="number" value={p.followers} onChange={(e) => updatePlatform(i, "followers", e.target.value)} placeholder="Followers (cth: 25000)" className={inputCls + " sm:col-span-2"} />
                      <input data-testid={`platform-er-${i}`} type="number" step="0.1" value={p.er} onChange={(e) => updatePlatform(i, "er", e.target.value)} placeholder="ER %" className={inputCls} />
                    </div>
                    {Number(p.followers) > 0 && (
                      <p className="text-xs text-sk-muted mt-2">{getTier(Number(p.followers)).label} · {String(p.er).trim() ? "harga dari ER kamu" : "ER dikosongkan → estimasi range"}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Formats */}
            <div className="rounded-2xl border border-sk-line bg-sk-surface p-6 shadow-sm">
              <h3 className="font-bold text-sk-cream mb-1">Jenis konten yang ditawarkan</h3>
              <p className="text-xs text-sk-muted mb-4">Pilih format yang mau kamu tawarkan ke brand.</p>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {FORMATS.map((f) => {
                  const on = formats.includes(f.key);
                  return (
                    <button key={f.key} data-testid={`format-${f.key}`} onClick={() => toggleFormat(f.key)} className={`text-left rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${on ? "border-sk-brand bg-sk-brand/10 text-sk-cream" : "border-sk-line bg-sk-bg text-sk-muted hover:text-sk-cream"}`}>
                      <span className={`inline-block h-4 w-4 rounded border mr-2 align-middle ${on ? "bg-sk-brand border-sk-brand" : "border-sk-muted"}`} />
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="text-xs text-sk-muted leading-relaxed rounded-xl border border-sk-line bg-sk-surface p-4" data-testid="ratecard-disclaimer">
              Rate dihitung otomatis berdasarkan followers & engagement rate. Gunakan sebagai starting point negosiasi, bukan harga final — usage rights, eksklusivitas, dan kompleksitas kreatif bisa mengubah harga.
            </p>
          </div>

          {/* PREVIEW */}
          <div className="lg:sticky lg:top-6 space-y-4">
            <div ref={previewRef} data-testid="ratecard-preview" className="bg-white rounded-2xl overflow-hidden border border-sk-line" style={{ boxShadow: "0 1px 2px rgba(36,28,23,0.06)" }}>
              {/* accent bar */}
              <div style={{ height: 6, background: "#DA6C46" }} />
              <div className="p-6 sm:p-7">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 pb-5 border-b" style={{ borderColor: "#EADFCE" }}>
                  <div className="min-w-0">
                    <p style={{ color: "#DA6C46", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Rate Card{creator.niche ? ` · ${creator.niche}` : ""}</p>
                    <h2 className="font-extrabold tracking-tight" style={{ color: "#241C17", fontSize: 26, lineHeight: 1.1, marginTop: 6 }}>{creator.name || "Nama Kreator"}</h2>
                    {topPlatform?.handle && <p style={{ color: "#837568", fontSize: 14, marginTop: 3 }}>{topPlatform.handle}</p>}
                    {creator.tagline && <p style={{ color: "#241C17", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>{creator.tagline}</p>}
                  </div>
                  {tier && (
                    <span style={{ background: "#FBEDE6", color: "#C55B39", border: "1px solid #EAD0C4", borderRadius: 999, display: "inline-block", padding: "0px 15px 13px", fontSize: 12, fontWeight: 700, lineHeight: 1, whiteSpace: "nowrap", boxSizing: "border-box", width: "auto", flexShrink: 0, textAlign: "center" }}>{tier.label}</span>
                  )}
                </div>

                {/* Audience */}
                {validPlatforms.length > 0 && (
                  <div style={{ paddingTop: 18 }}>
                    <p style={{ color: "#837568", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 10 }}>Audiens</p>
                    <div className="grid grid-cols-2 gap-3">
                      {validPlatforms.map((p, i) => (
                        <div key={i} style={{ background: "#FBF6EF", border: "1px solid #EADFCE", borderRadius: 12, padding: "12px 14px" }}>
                          <p style={{ color: "#241C17", fontSize: 13, fontWeight: 700 }}>{p.platform}</p>
                          <p style={{ color: "#241C17", fontSize: 18, fontWeight: 800, marginTop: 2 }}>{Number(p.followers).toLocaleString("id-ID")}</p>
                          <p style={{ color: "#837568", fontSize: 12, marginTop: 1 }}>followers · ER {String(p.er).trim() ? `${p.er}%` : "est."}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rates */}
                {validPlatforms.length > 0 && selectedFormats.length > 0 ? (
                  <div style={{ paddingTop: 20 }}>
                    <p style={{ color: "#837568", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 10 }}>Daftar Harga</p>
                    <div className="space-y-4">
                      {validPlatforms.map((p, pi) => {
                        const usesEr = String(p.er).trim() !== "";
                        return (
                          <div key={pi}>
                            {validPlatforms.length > 1 && (
                              <p style={{ color: "#241C17", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{p.platform}</p>
                            )}
                            <div>
                              {selectedFormats.map((f) => {
                                const r = computeRate(p.followers, p.er, f.mult);
                                return (
                                  <div key={f.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #F3EADB" }}>
                                    <span style={{ color: "#241C17", fontSize: 13 }}>{f.label}</span>
                                    <span style={{ color: "#241C17", fontSize: 14, fontWeight: 800, whiteSpace: "nowrap", marginLeft: 12 }}>{r ? r.display : "-"}</span>
                                  </div>
                                );
                              })}
                            </div>
                            <p style={{ color: "#837568", fontSize: 11, marginTop: 6, lineHeight: "14px" }}>
                              <span style={{ color: usesEr ? "#7C8A57" : "#DA6C46", fontWeight: 700 }}>•</span>{" "}
                              {usesEr ? "Berdasarkan ER kamu" : "Estimasi awal (isi ER untuk angka pasti)"}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div style={{ paddingTop: 24, paddingBottom: 12, textAlign: "center" }}>
                    <p style={{ color: "#837568", fontSize: 13 }}>Isi followers & pilih jenis konten untuk melihat daftar harga.</p>
                  </div>
                )}

                {/* Footer */}
                <div style={{ marginTop: 22, paddingTop: 14, borderTop: "1px solid #EADFCE", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                  <span style={{ color: "#837568", fontSize: 11 }}>Generated with Studio Kreator</span>
                  <span style={{ color: "#837568", fontSize: 11 }}>{creator.email ? `${creator.email} · ` : ""}{creator.location ? `${creator.location} · ` : ""}{today}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2.5">
              <button data-testid="export-pdf-btn" onClick={exportPDF} disabled={exporting} className="flex items-center justify-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold py-3 text-sm hover:bg-sk-brandDark transition-colors disabled:opacity-60"><Download className="h-4 w-4" /> Download PDF</button>
              <button data-testid="export-png-btn" onClick={exportPNG} disabled={exporting} className="flex items-center justify-center gap-2 rounded-full border border-sk-line py-3 text-sm font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors disabled:opacity-60"><ImageIcon className="h-4 w-4" /> Simpan Gambar</button>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <button data-testid="save-ratecard-btn" onClick={save} className="flex items-center justify-center gap-2 rounded-full border border-sk-line py-2.5 text-sm font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors"><Save className="h-4 w-4" /> {currentId ? "Perbarui" : "Simpan ke Akun"}</button>
              <button data-testid="new-ratecard-btn" onClick={resetForm} className="flex items-center justify-center gap-2 rounded-full border border-sk-line py-2.5 text-sm font-semibold text-sk-muted hover:text-sk-cream transition-colors"><Plus className="h-4 w-4" /> Rate Card Baru</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
